import React from "react";
import { motion } from "framer-motion";
import { Flame, ArrowDown, Wind, Droplets } from "lucide-react";
import { AnimatedNumber } from "@/components/common/Widgets";
import { fmtNum } from "@/lib/format";

function FlowColumn({ color }) {
  return (
    <div className="relative h-7 w-px mx-auto overflow-hidden" style={{ background: `${color}33` }}>
      {[0, 1, 2].map((i) => (
        <div key={i} className="flow-particle absolute left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
          style={{ background: color, animationDelay: `${i * 0.7}s` }} />
      ))}
    </div>
  );
}

function Stage({ icon, label, value, unit, color, sub }) {
  return (
    <div className="panel-2 w-full px-4 py-3 flex items-center gap-3 border-l-2" style={{ borderLeftColor: color }}>
      <div className="w-9 h-9 flex items-center justify-center shrink-0" style={{ background: `${color}1f`, color }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="eyebrow">{label}</div>
        {sub && <div className="text-[0.7rem] text-ink-soft">{sub}</div>}
      </div>
      <div className="text-right">
        <span className="text-xl font-bold tnum" style={{ color }}>
          <AnimatedNumber value={value} decimals={value < 100 ? 1 : 0} />
        </span>
        <span className="text-[0.7rem] text-ink-soft ml-1 font-mono">{unit}</span>
      </div>
    </div>
  );
}

export function BoilerTwinDiagram({ data }) {
  if (!data) return null;
  const eff = data.boiler.efficiency_pct;
  const effColor = eff >= 84 ? "#33b76d" : eff >= 80 ? "#fab20b" : "#E63946";
  const lossPct = data.losses.total_loss_pct;

  return (
    <div className="flex flex-col items-center gap-0 w-full" data-testid="boiler-twin-diagram">
      <Stage icon={<ArrowDown size={18} />} label="Coal In" value={data.coal.flow_tph} unit="t/hr" color="#8BA1C0"
        sub={`GCV ${fmtNum(data.coal.gcv)} kcal/kg`} />
      <FlowColumn color="#8BA1C0" />
      <Stage icon={<Flame size={18} />} label="Combustion" value={data.boiler.combustion_eff} unit="%" color="#fab20b"
        sub={`O₂ ${fmtNum(data.boiler.o2_pct, 1)}% · EA ${fmtNum(data.boiler.excess_air_pct)}%`} />
      <FlowColumn color="#fab20b" />
      <Stage icon={<Wind size={18} />} label="Steam Out" value={data.boiler.steam_flow_tph} unit="t/hr" color="#33b76d"
        sub={`${fmtNum(data.boiler.steam_pressure_bar, 1)} bar · ${fmtNum(data.boiler.steam_temp_c)}°C`} />
      <FlowColumn color="#E63946" />
      <Stage icon={<Droplets size={18} />} label="Losses" value={lossPct} unit="%" color="#E63946"
        sub={`Stack ${fmtNum(data.losses.stack_pct, 1)}% · Blowdown ${fmtNum(data.losses.blowdown_pct, 1)}%`} />

      <div className="w-full mt-4 panel-2 px-4 py-4">
        <div className="flex items-end justify-between">
          <div>
            <div className="eyebrow">Boiler Efficiency</div>
            <div className="text-3xl font-bold tnum" style={{ color: effColor }}>
              <AnimatedNumber value={eff} decimals={1} />%
            </div>
          </div>
          <div className="text-right">
            <div className="eyebrow">Expected</div>
            <div className="text-lg font-bold tnum text-ink-soft">{fmtNum(data.boiler.expected_efficiency_pct, 1)}%</div>
            <div className="text-xs font-mono mt-0.5" style={{ color: "#E63946" }}>Gap −{fmtNum(data.boiler.efficiency_gap, 1)}%</div>
          </div>
        </div>
        <div className="mt-3 h-2 w-full bg-[#16223d] relative">
          <motion.div className="h-full" style={{ background: effColor }}
            initial={{ width: 0 }} animate={{ width: `${eff}%` }} transition={{ duration: 0.8 }} />
          <div className="absolute top-0 h-full w-px bg-ink-soft" style={{ left: `${data.boiler.expected_efficiency_pct}%` }} />
        </div>
      </div>
    </div>
  );
}
